import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Shield,
  Phone,
  TreePine,
  Palette,
  Leaf,
  Award
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PaulowniaPage = () => {
  const features = [
    { text: 'Lightweight Hardwood Encased in Polymer', icon: TreePine },
    { text: 'Hard Wearing Polymer Casing', icon: Shield },
    { text: 'Lightweight Yet Durable', icon: Shield },
    { text: 'Natural Wood Grain Beauty', icon: TreePine },
    { text: 'Sustainable & Eco-Friendly', icon: Leaf },
    { text: 'Smooth Operation & Finish', icon: Award },
    { text: 'Available in 30+ Colours', icon: Palette },
    { text: '6 Natural Wood Stains Available', icon: Palette },
    { text: 'Custom Colours Available', icon: Palette },
    { text: '10 Year Structural Warranty', icon: Shield }
  ];

  const bestFor = [
    'Living rooms',
    'Bedrooms',
    'Dining rooms',
    'Study areas',
    'Home offices',
    'Period properties'
  ];

  const faqs = [
    {
      question: 'What are Paulownia+ Shutters?',
      answer: 'Paulownia+ shutters feature a lightweight hardwood core encased in a hard wearing polymer casing. This innovative design combines the natural beauty and warmth of real hardwood with the durability and low maintenance benefits of polymer.'
    },
    {
      question: 'Why is Paulownia+ our recommended option?',
      answer: 'Paulownia+ offers the perfect combination of quality, durability and value. The polymer casing provides excellent protection against everyday wear and tear, while the hardwood core ensures a premium feel. It delivers outstanding performance backed by a 10-year warranty.'
    },
    {
      question: 'How do I care for Paulownia+ Shutters?',
      answer: 'The polymer casing makes Paulownia+ shutters very low maintenance. Simply wipe down with a damp cloth for regular cleaning. The hard wearing exterior resists scratches and scuffs, keeping them looking beautiful for years.'
    },
    {
      question: 'Can I get a custom colour match?',
      answer: 'Absolutely. While we offer over 30 standard colours and 6 natural wood stains, we can also create custom colour matches to perfectly complement your interior design scheme.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20" style={{background: 'linear-gradient(to bottom right, #f0f7f2, #e5f0e8)'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 mb-6">
                <TreePine className="h-5 w-5 text-green-600" />
                <Badge className="bg-green-600 text-white">Best Quality & Price</Badge>
                <Badge className="bg-amber-500 text-white ml-2">Recommended</Badge>
              </div>
              
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Paulownia+ Shutters
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Lightweight hardwood shutters encased in a hard wearing polymer casing. The perfect 
                combination of natural beauty, durability and exceptional value for everyday living spaces.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Shield className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-gray-900">10 Year Warranty</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Leaf className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-gray-900">FSC Certified</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg">
                    <Calculator className="mr-2 h-5 w-5" />
                    Use Our Online Estimator
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-4 text-lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Book Free Survey
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-amber-500 text-white px-4 py-2 rounded-full font-semibold shadow-lg z-10">
                Most Popular Choice
              </div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Paulownia Timber Shutters"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Banner */}
      <section className="py-8 bg-green-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div className="flex flex-col items-center gap-2">
              <TreePine className="h-8 w-8" />
              <span className="font-semibold">Real Wood Beauty</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Leaf className="h-8 w-8" />
              <span className="font-semibold">Eco-Friendly</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Palette className="h-8 w-8" />
              <span className="font-semibold">30+ Colours</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8" />
              <span className="font-semibold">10 Year Warranty</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Features</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Paulownia+ Shutters combine natural hardwood with a durable polymer casing for outstanding performance.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-green-50">
                      <feature.icon className="h-6 w-6 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium pt-2">{feature.text}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Perfect For Any Room</h2>
              <p className="text-lg text-gray-600 mb-8">
                Paulownia+ Shutters bring natural warmth and elegance to any interior space. 
                Their hard wearing polymer casing makes them versatile and low maintenance for both traditional and contemporary homes.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {bestFor.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <TreePine className="h-5 w-5 text-green-600" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Paulownia Shutters in Living Room"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Colour Options */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Extensive Colour Range</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose from over 30 painted colours or 6 natural wood stains to perfectly match your interior.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Palette className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">30+ Painted Colours</h3>
                <p className="text-gray-600">From classic whites to bold statement colours</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <TreePine className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">6 Wood Stains</h3>
                <p className="text-gray-600">Showcase the natural wood grain beauty</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Award className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Custom Matching</h3>
                <p className="text-gray-600">Perfect colour match to your specification</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about our Paulownia+ Shutters
            </p>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">The Smart Choice for Quality & Value</h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant estimate for Paulownia+ Shutters using our online calculator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/materials">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Compare All Ranges
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PaulowniaPage;
